import { Fragment, type ReactNode } from 'react';

import { Group, Stack, Text, Title } from '@mantine/core';

import type { RegistryDocumentDigest } from '@/types/registry-documents';
import { formatRegistryMessageTime } from '../RegistryOrdersCard/RegistryOrderMessages';
import { DigestCombinedView } from './DigestCombinedView';
import { DigestDocumentEntry } from './DigestDocumentEntry';
import {
  documentEntryHeading,
  documentEntryKey,
  documentSectionTitle,
  isPerDocumentDigest,
} from './digestDocuments';
import {
  CorporateInformation,
  DigestAppendix,
  DocumentInformation,
  FinancialInformation,
} from './DigestSections';
import {
  coverageNotes,
  failedChunksSentence,
  hasCorporateInformation,
  officersDroppedNote,
  registryNameOf,
  TEMPLATE_WORDS,
} from './digestTemplate';

/** The heading the actions sit beside when the digest has no section to
 * show (every model call on the only document failed). */
const FALLBACK_TITLE = 'Document information';

interface RegistryDigestViewProps {
  digest: RegistryDocumentDigest;
  /** The order's company name: what the lead-ins fall back to when the
   * documents give no registered name. */
  companyName: string;
  /** When the documents were delivered, formatted: the sources' retrieved
   * date when a source gives none. */
  deliveredAt?: string | null;
  /** The buttons beside the first heading: copy, download, run again. */
  actions?: ReactNode;
}

interface DigestSectionPart {
  key: string;
  title: string;
  body: ReactNode;
}

/** A version 2 digest's sections in the Word file's order: Corporate
 * Information when a corporate document was read (or the digest predates
 * roles), Financial Information when accounts were, then one per other
 * document. */
function orderWideSections(
  digest: RegistryDocumentDigest,
  registry: string,
  companyName: string
): DigestSectionPart[] {
  const sections: DigestSectionPart[] = [];
  if (hasCorporateInformation(digest)) {
    sections.push({
      key: 'corporate',
      title: TEMPLATE_WORDS.title,
      body: (
        <CorporateInformation corporate={digest} registry={registry} companyName={companyName} />
      ),
    });
  }
  if (digest.financials) {
    sections.push({
      key: 'financials',
      title: digest.financials.title,
      body: <FinancialInformation financials={digest.financials} registry={registry} />,
    });
  }
  digest.documents.forEach((section, index) => {
    sections.push({
      key: documentEntryKey(section, index),
      title: documentSectionTitle(section),
      body: <DocumentInformation section={section} />,
    });
  });
  return sections;
}

/** A version 3 digest's sections: one per read document, in delivery
 * order, keyed by the file's hash. */
function perDocumentSections(
  digest: RegistryDocumentDigest,
  registry: string,
  companyName: string,
  deliveredAt: string | null
): DigestSectionPart[] {
  return digest.documents.map((section, index) => ({
    key: documentEntryKey(section, index),
    title: documentEntryHeading(section, registry),
    body: (
      <DigestDocumentEntry
        section={section}
        registry={registry}
        companyName={companyName}
        deliveredAt={deliveredAt}
      />
    ),
  }));
}

/** A section's title; the first one carries the actions beside it. */
function SectionHeading({
  title,
  first,
  actions,
}: Readonly<{ title: string; first: boolean; actions?: ReactNode }>) {
  if (!first) {
    return <Title order={5}>{title}</Title>;
  }
  return (
    <Group justify="space-between" align="flex-start" gap="xs" wrap="wrap">
      <Title order={5}>{title}</Title>
      {actions}
    </Group>
  );
}

/**
 * A completed digest laid out as the report takes it: a line per
 * document the desk could not supply (a trace report instead), then one
 * section per delivered document, headed "{document title} ({registry},
 * {issued})", with the issuer, the summary, the body its role gives (the
 * corporate tables, the financial figures, the key facts), its sources
 * and its "Note:" lines. After them, what the model calls left unread,
 * the order-wide tables merged from every document folded under a
 * "Combined view" toggle, and the run's footer. Data in and markup out:
 * the hook and the section around it own every action.
 *
 * A digest from before the per-document entries (schema version 2 or 1)
 * lays out the order-wide sections as it always did: Corporate
 * Information, Financial Information, a section per other document, what
 * was not read, the caveats and the sources appendix.
 */
export function RegistryDigestView({
  digest,
  companyName,
  deliveredAt = null,
  actions,
}: Readonly<RegistryDigestViewProps>) {
  const registry = registryNameOf(digest);
  const perDocument = isPerDocumentDigest(digest);
  const sections = perDocument
    ? perDocumentSections(digest, registry, companyName, deliveredAt)
    : orderWideSections(digest, registry, companyName);
  // Each entry says what was not read of its own file; order-wide only
  // the failed model calls are left to say.
  const failed = failedChunksSentence(digest.coverage);
  const notRead = perDocument ? (failed === null ? [] : [failed]) : coverageNotes(digest);
  const generated = formatRegistryMessageTime(digest.coverage.generatedAt);
  const footer = [
    generated && `Generated ${generated}`,
    digest.coverage.model,
    officersDroppedNote(digest.coverage),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Stack gap="sm" data-testid="registry-digest-view">
      {digest.notices.map((notice, index) => (
        // Notices carry no id; the list is as the digest ordered it.
        <Text key={`${index}:${notice}`} size="sm" data-testid="registry-digest-notice">
          {notice}
        </Text>
      ))}
      {sections.length === 0 && <SectionHeading title={FALLBACK_TITLE} first actions={actions} />}
      {sections.map((section, index) => (
        <Fragment key={section.key}>
          <SectionHeading title={section.title} first={index === 0} actions={actions} />
          {section.body}
        </Fragment>
      ))}

      {notRead.map((sentence) => (
        <Text key={sentence} size="sm" c="orange" data-testid="registry-digest-coverage">
          {sentence}
        </Text>
      ))}
      {perDocument ? (
        <DigestCombinedView
          digest={digest}
          registry={registry}
          companyName={companyName}
          deliveredAt={deliveredAt}
        />
      ) : (
        <DigestAppendix digest={digest} deliveredAt={deliveredAt} />
      )}
      {footer && (
        <Text size="xs" c="dimmed">
          {footer}
        </Text>
      )}
    </Stack>
  );
}
